import React from "react";
import { FileText, Building, Mail } from "lucide-react";

const Terms = () => {

  return (
    <div className="min-h-screen bg-white">
      {/* Calm cream intro band */}
      <section
        className="bg-cream pt-16 pb-16 md:pt-24 md:pb-20 px-6"
      >
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-primary text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-5">
            Legal
          </p>
          <h1 className="font-display font-light text-ink leading-[1.1] text-4xl md:text-5xl mb-8">
            Terms of Service
          </h1>
          <div className="bg-white rounded-2xl border border-line p-6 text-left max-w-2xl mx-auto">
            <div className="space-y-2 text-sm text-muted">
              <p>
                <strong className="text-ink">Effective date:</strong> 02 May
                2024
              </p>
              <p>
                <strong className="text-ink">Last updated:</strong> 02 May
                2024
              </p>
              <p>
                <strong className="text-ink">Operating business name:</strong>{" "}
                Wei Care
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Company Information */}
      <section className="py-16 md:py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-cream rounded-2xl p-8 mb-12">
            <h2 className="font-display font-light text-ink text-2xl md:text-3xl mb-6 flex items-center">
              <Building
                className="w-6 h-6 mr-3 text-primary"
                strokeWidth={1.5}
              />
              Company Information
            </h2>
            <div className="space-y-3 text-muted">
              <p>
                <strong className="text-ink">Business name:</strong> Wei Care
                Enterprise
              </p>
              <p>
                <strong className="text-ink">ABN:</strong> 40 643 964 121
              </p>
              <p>
                <strong className="text-ink">Address:</strong> 15 Monash Street, Clermont QLD 4721
              </p>
              <p>
                <strong className="text-ink">Domain:</strong>{" "}
                weicarehome.com
              </p>
              <p>
                <strong className="text-ink">Contact:</strong>{" "}
                info@weicarehome.com
              </p>
            </div>
          </div>

          <p className="text-muted text-base leading-relaxed">
            These Terms of Service ("Terms") govern use of our website and the
            provision of home and aged care services to individuals and
            families. By using the site or engaging our services you accept
            these Terms.
          </p>
        </div>
      </section>

      {/* Definitions */}
      <section className="py-16 md:py-20 px-6 bg-cream">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-light text-ink text-3xl md:text-4xl mb-8">
            1) Definitions
          </h2>
          <div className="space-y-4">
            {[
              {
                term: "Provider, we, us, our",
                definition: "Wei Care.",
              },
              {
                term: "You, client",
                definition:
                  "the person who visits the site, the people we support, or the family member or representative who engages our services.",
              },
              {
                term: "Services",
                definition:
                  "home and aged care support, including personal care, domestic assistance, community access, and related coordination and administration.",
              },
              {
                term: "Site",
                definition:
                  "weicarehome.com, its subdomains, portals, forms, emails, and messaging channels operated by us.",
              },
              {
                term: "Care team",
                definition:
                  "the support workers, coordinators, and other staff who deliver or arrange your care.",
              },
              {
                term: "Third-party costs",
                definition:
                  "amounts charged by external providers such as allied health professionals, equipment suppliers, transport, and similar services.",
              },
              {
                term: "Business day",
                definition:
                  "Monday to Friday, excluding Queensland public holidays.",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 border border-line"
              >
                <p className="text-muted">
                  <strong className="text-ink">{item.term}:</strong>{" "}
                  {item.definition}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-16 md:py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-light text-ink text-3xl md:text-4xl mb-8">
            2) Who we are and what we do
          </h2>
          <div className="space-y-6">
            <div className="bg-cream rounded-2xl p-6">
              <p className="text-muted leading-relaxed">
                <strong className="text-ink">2.1</strong> We are a home and
                aged care provider supporting individuals and families. We help
                people live well at home and in their community through personal
                care, domestic assistance, and related support. Where a service
                is best delivered by an external professional (for example
                allied health or specialist clinical care), we coordinate with
                suitable providers who are engaged under their own terms.
              </p>
            </div>
            <div className="bg-cream rounded-2xl p-6">
              <p className="text-muted leading-relaxed">
                <strong className="text-ink">2.2</strong> Our care is
                person-centred and planned with you. The support we provide is
                set out in your care plan and engagement documents, and we
                review it with you as your needs change.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Scope of Terms */}
      <section className="py-16 md:py-20 px-6 bg-cream">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-light text-ink text-3xl md:text-4xl mb-8">
            3) Scope of these Terms
          </h2>
          <p className="text-muted leading-relaxed mb-6">
            These Terms have three parts.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl p-6 border border-line border-l-4 border-l-primary">
              <h3 className="font-display font-normal text-ink text-xl mb-2">
                Part A
              </h3>
              <p className="text-muted text-sm">
                Website Terms apply to all visitors.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-line border-l-4 border-l-primary">
              <h3 className="font-display font-normal text-ink text-xl mb-2">
                Part B
              </h3>
              <p className="text-muted text-sm">
                Services Terms apply when you request care services.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-line border-l-4 border-l-primary">
              <h3 className="font-display font-normal text-ink text-xl mb-2">
                Part C
              </h3>
              <p className="text-muted text-sm">
                General Legal Terms apply to both.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Part A - Website Terms */}
      <section className="py-16 md:py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-light text-ink text-3xl md:text-4xl mb-8 border-l-4 border-primary pl-4">
            Part A: Website Terms
          </h2>

          <div className="space-y-12">
            {/* Use of the site */}
            <div>
              <h3 className="font-display font-normal text-ink text-xl mb-4">
                4) Use of the site
              </h3>
              <div className="space-y-4">
                <p className="text-muted leading-relaxed">
                  <strong className="text-ink">4.1</strong> Use the site
                  lawfully, for its intended purpose, and in accordance with
                  these Terms. Do not attempt security testing, scraping,
                  reverse engineering, or any activity that harms the site or
                  users.
                </p>
                <p className="text-muted leading-relaxed">
                  <strong className="text-ink">4.2</strong> Where an account
                  is offered, keep credentials confidential. You are responsible
                  for activity under your account.
                </p>
              </div>
            </div>

            {/* Information on the site */}
            <div>
              <h3 className="font-display font-normal text-ink text-xl mb-4">
                5) Information on the site
              </h3>
              <div className="space-y-4">
                <p className="text-muted leading-relaxed">
                  <strong className="text-ink">5.1</strong> Site content is
                  general information for guidance only. It is not medical or
                  clinical advice and it is not a substitute for an assessment
                  of your individual needs. Care is always tailored to your
                  personal circumstances.
                </p>
                <p className="text-muted leading-relaxed">
                  <strong className="text-ink">5.2</strong> We try to keep
                  content accurate but do not promise completeness, timeliness,
                  or error-free material.
                </p>
              </div>
            </div>

            {/* User content */}
            <div>
              <h3 className="font-display font-normal text-ink text-xl mb-4">
                6) User content
              </h3>
              <div className="space-y-4">
                <p className="text-muted leading-relaxed">
                  <strong className="text-ink">6.1</strong> If you upload or
                  submit content you promise you have the right to share it and
                  that it is accurate and lawful.
                </p>
                <p className="text-muted leading-relaxed">
                  <strong className="text-ink">6.2</strong> You grant us a
                  limited licence to store and process that content to operate
                  the site and deliver requested Services. See our Privacy
                  Policy for details.
                </p>
              </div>
            </div>

            {/* Intellectual property */}
            <div>
              <h3 className="font-display font-normal text-ink text-xl mb-4">
                7) Intellectual property
              </h3>
              <div className="space-y-4">
                <p className="text-muted leading-relaxed">
                  <strong className="text-ink">7.1</strong> The site,
                  branding, text, graphics, and templates are owned by us or our
                  licensors. You may not copy, sell, or reuse any part without
                  permission.
                </p>
                <p className="text-muted leading-relaxed">
                  <strong className="text-ink">7.2</strong> You may download
                  materials we provide for your personal, non-commercial use
                  related to your care.
                </p>
              </div>
            </div>

            {/* Third-party links */}
            <div>
              <h3 className="font-display font-normal text-ink text-xl mb-4">
                8) Third-party links
              </h3>
              <p className="text-muted leading-relaxed">
                The site may link to third parties. We do not control or endorse
                their content or practices. Use them at your discretion.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Part B - Services Terms */}
      <section className="py-16 md:py-20 px-6 bg-cream">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-light text-ink text-3xl md:text-4xl mb-8 border-l-4 border-primary pl-4">
            Part B: Services Terms
          </h2>

          <div className="space-y-12">
            {/* Engagement process */}
            <div>
              <h3 className="font-display font-normal text-ink text-xl mb-4">
                9) Engagement process
              </h3>
              <div className="space-y-4">
                <p className="text-muted leading-relaxed">
                  <strong className="text-ink">9.1</strong> An engagement
                  begins when you accept a written service agreement, letter of
                  engagement, or quote that references these Terms, and care
                  commences as arranged.
                </p>
                <p className="text-muted leading-relaxed">
                  <strong className="text-ink">9.2</strong> The specific
                  support, schedule, and fees for your care will appear in your
                  service agreement and care plan. If there is a conflict, your
                  service agreement prevails over these Terms.
                </p>
              </div>
            </div>

            {/* Our role */}
            <div>
              <h3 className="font-display font-normal text-ink text-xl mb-4">
                10) Our role
              </h3>
              <div className="space-y-4">
                <p className="text-muted leading-relaxed">
                  <strong className="text-ink">10.1</strong> We provide home
                  and aged care support and coordination, which may include:
                  needs assessments, care planning, personal care, domestic
                  assistance, meal support, community access and transport,
                  social support, and ongoing review of your care.
                </p>
                <p className="text-muted leading-relaxed">
                  <strong className="text-ink">10.2</strong> Where a service
                  is best delivered by an external professional, such as allied
                  health or specialist clinical care, we will help arrange a
                  suitable provider. You may contract with that provider
                  directly and pay their fees separately unless your service
                  agreement states otherwise. We remain your day-to-day point of
                  contact unless agreed differently.
                </p>
              </div>
            </div>

            {/* Your responsibilities */}
            <div>
              <h3 className="font-display font-normal text-ink text-xl mb-4">
                11) Your responsibilities
              </h3>
              <p className="text-muted leading-relaxed mb-4">You agree to:</p>
              <ul className="space-y-2 text-muted">
                <li className="flex items-start">
                  <span className="text-primary font-semibold mr-2">a)</span>
                  <span>
                    Provide complete and accurate information and documents in a
                    timely manner.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary font-semibold mr-2">b)</span>
                  <span>
                    Tell us promptly about changes to your health, needs,
                    schedule, or contact details.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary font-semibold mr-2">c)</span>
                  <span>
                    Review and agree your care plan and let us know if anything
                    needs to change.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary font-semibold mr-2">d)</span>
                  <span>
                    Pay our invoices and any third-party costs that are your
                    responsibility.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary font-semibold mr-2">e)</span>
                  <span>
                    Provide a safe working environment for our care team in your
                    home.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary font-semibold mr-2">f)</span>
                  <span>Treat our care team with courtesy and respect.</span>
                </li>
              </ul>
            </div>

            {/* Service expectations */}
            <div className="bg-white border border-line rounded-2xl p-6">
              <h3 className="font-display font-normal text-ink text-xl mb-4">
                12) Service expectations
              </h3>
              <div className="space-y-4">
                <p className="text-muted leading-relaxed">
                  <strong className="text-ink">12.1</strong> We deliver care
                  with reasonable skill and care, in line with your care plan.
                  Health outcomes depend on many factors, so we cannot guarantee
                  specific clinical or health results.
                </p>
                <p className="text-muted leading-relaxed">
                  <strong className="text-ink">12.2</strong> Visit times and
                  schedules we share are arranged in good faith and may
                  occasionally change due to staff availability, your needs, or
                  circumstances beyond our control. We will let you know as soon
                  as we reasonably can.
                </p>
              </div>
            </div>

            {/* Fees, expenses, and taxes */}
            <div>
              <h3 className="font-display font-normal text-ink text-xl mb-4">
                13) Fees, expenses, and taxes
              </h3>
              <div className="space-y-4">
                <p className="text-muted leading-relaxed">
                  <strong className="text-ink">13.1</strong> Our fees are set
                  out in your service agreement and may be charged per visit,
                  per hour, or per agreed package. Where funding applies,
                  charges are handled in line with your funding arrangements.
                </p>
                <p className="text-muted leading-relaxed">
                  <strong className="text-ink">13.2</strong> Third-party costs
                  such as allied health services, equipment, consumables, and
                  transport are not included unless stated. You pay these
                  directly where possible.
                </p>
                <p className="text-muted leading-relaxed">
                  <strong className="text-ink">13.3</strong> Prices are shown
                  before taxes unless stated. You are responsible for any
                  applicable taxes, such as GST where it applies.
                </p>
                <p className="text-muted leading-relaxed">
                  <strong className="text-ink">13.4</strong> Invoices are due
                  as stated on the invoice. We may pause non-urgent services for
                  overdue invoices after giving you reasonable notice, and we
                  will always act in line with our duty of care.
                </p>
                <p className="text-muted leading-relaxed">
                  <strong className="text-ink">13.5</strong>{" "}
                  <strong className="text-ink">
                    Currency and payment methods.
                  </strong>{" "}
                  Unless stated otherwise, charges are in Australian dollars
                  (AUD) and payable via the methods listed on your invoice. Any
                  bank charges are your responsibility.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Part C - General Legal Terms */}
      <section className="py-16 md:py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-light text-ink text-3xl md:text-4xl mb-8 border-l-4 border-primary pl-4">
            Part C: General Legal Terms
          </h2>

          <div className="space-y-12">
            {/* Privacy */}
            <div>
              <h3 className="font-display font-normal text-ink text-xl mb-4">
                24) Privacy
              </h3>
              <p className="text-muted leading-relaxed">
                Our handling of personal and health information is described in
                our Privacy Policy. By using the Site or Services you
                acknowledge that policy. We handle your information in line with
                Australian privacy law.
              </p>
            </div>

            {/* Limitation of liability */}
            <div className="bg-cream rounded-2xl p-6">
              <h3 className="font-display font-normal text-ink text-xl mb-4">
                26) Limitation of liability
              </h3>
              <div className="space-y-4">
                <p className="text-muted leading-relaxed">
                  <strong className="text-ink">26.1</strong> To the fullest
                  extent permitted by law, our total liability for all claims
                  arising out of or related to the Site or Services will not
                  exceed the fees you paid to us for the Services giving rise to
                  the claim during the 12 months before the event.
                </p>
                <p className="text-muted leading-relaxed">
                  <strong className="text-ink">26.2</strong> We are not liable
                  for indirect, incidental, special, consequential, or punitive
                  damages, loss of profits, loss of business, or loss of data.
                </p>
                <p className="text-muted leading-relaxed">
                  <strong className="text-ink">26.3</strong> Nothing in these
                  Terms limits liability for death or personal injury caused by
                  negligence, fraud, or other liability that cannot be excluded
                  by law.
                </p>
              </div>
            </div>

            {/* Governing law and disputes */}
            <div>
              <h3 className="font-display font-normal text-ink text-xl mb-4">
                29) Governing law and disputes
              </h3>
              <div className="space-y-4">
                <p className="text-muted leading-relaxed">
                  <strong className="text-ink">29.1</strong> If your service
                  agreement specifies governing law and forum, that clause
                  controls. If not, the law of Queensland
                  governs these Terms and the courts of Queensland have
                  jurisdiction.
                </p>
                <p className="text-muted leading-relaxed">
                  <strong className="text-ink">29.2</strong> You may have
                  mandatory rights under Australian consumer law that override
                  parts of this section. Nothing in these Terms limits those
                  rights.
                </p>
                <p className="text-muted leading-relaxed">
                  <strong className="text-ink">29.3</strong> Before filing a
                  claim, each party will attempt good faith resolution for at
                  least 30 days after written notice of the dispute.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Country-specific disclosures */}
      <section className="py-16 md:py-20 px-6 bg-cream">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-light text-ink text-3xl md:text-4xl mb-8">
            Additional disclosures
          </h2>

          <div className="space-y-8">
            {/* Provider */}
            <div className="bg-white rounded-2xl p-6 border border-line">
              <h3 className="font-display font-normal text-ink text-xl mb-4">
                Provider
              </h3>
              <div className="space-y-3 text-muted">
                <p>
                  Services are provided by Weicare Pty Ltd, trading as Wei Care (ABN 40 643 964 121), based in 15 Monash Street, Clermont QLD 4721.
                </p>
                <p>
                  <strong className="text-ink">Complaints:</strong>{" "}
                  info@weicarehome.com. We welcome your feedback and take
                  concerns seriously. We aim to acknowledge complaints within 3
                  business days and respond within 14 business days. Your
                  service agreement sets out how to raise and escalate a
                  concern, and we will support you to do so.
                </p>
              </div>
            </div>

            {/* Your rights */}
            <div className="bg-white rounded-2xl p-6 border border-line">
              <h3 className="font-display font-normal text-ink text-xl mb-4">
                Your rights
              </h3>
              <p className="text-muted">
                We support the rights of the people we care for, including
                dignity, choice, privacy, and respect. Your information is
                handled in line with Australian privacy law, as described in our
                Privacy Policy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16 md:py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-light text-ink text-3xl md:text-4xl mb-8">
            Contact
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                title: "Questions about these Terms",
                email: "info@weicarehome.com",
                icon: <FileText className="w-5 h-5" strokeWidth={1.5} />,
              },
              {
                title: "Billing questions and cancellations",
                email: "info@weicarehome.com",
                icon: <Mail className="w-5 h-5" strokeWidth={1.5} />,
              },
              {
                title: "Privacy questions",
                email: "info@weicarehome.com",
                icon: <Mail className="w-5 h-5" strokeWidth={1.5} />,
              },
              {
                title: "Complaints",
                email: "info@weicarehome.com",
                icon: <Mail className="w-5 h-5" strokeWidth={1.5} />,
              },
            ].map((contact, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 border border-line"
              >
                <div className="flex items-center mb-3">
                  <div className="bg-accent-soft p-2 rounded-lg mr-3 text-primary-dark">
                    {contact.icon}
                  </div>
                  <h3 className="font-medium text-ink">{contact.title}</h3>
                </div>
                <a
                  href={`mailto:${contact.email}`}
                  className="text-primary hover:text-primary-dark transition-colors"
                >
                  {contact.email}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Online contract signing — added for Wei Care */}
      <section className="py-16 md:py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-light text-ink text-3xl md:text-4xl mb-8">
            Signing employment contracts online
          </h2>
          <div className="space-y-4 text-muted text-base leading-relaxed">
            <p>
              A signing link is personal to the person it was issued to. Do not share it or sign on
              someone else's behalf. A link stops working once the contract is signed or withdrawn.
            </p>
            <p>
              When you sign on this website you agree to sign electronically, and your electronic
              signature has the same effect as a handwritten one. You receive the signed contract by
              email and can download it straight after signing.
            </p>
            <p>
              The terms of your employment are those in your signed contract. If anything on this
              website differs from your contract, the contract applies.
            </p>
          </div>
        </div>
      </section>

      {/* Service Level Summary */}
      <section className="py-16 md:py-20 px-6 bg-cream">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-light text-ink text-2xl md:text-3xl mb-6">
            Optional annex: service level summary (non-contractual)
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl p-6 border border-line">
              <h4 className="font-medium text-ink mb-2">Response time</h4>
              <p className="text-muted text-sm">
                24 to 72 hours on business days
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-line">
              <h4 className="font-medium text-ink mb-2">Priority handling</h4>
              <p className="text-muted text-sm">
                Time-sensitive steps prioritised
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-line">
              <h4 className="font-medium text-ink mb-2">
                Availability window
              </h4>
              <p className="text-muted text-sm">
                Office hours 09:00 to 18:00 (Queensland time)
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;
